import { ReportKpiTile } from './report-kpi-tile';
import type { RateSet } from '../types/analytics-api.types';
import { fmtPct } from '../utils/report-format';
import { OPEN_CAVEAT, RATES } from '../utils/report-rates';
import type { ReportRateKey } from '../types/reports.types';

const RATE_KEYS: ReportRateKey[] = [
  'deliveryRate',
  'openRate',
  'clickRate',
  'clickToOpenRate',
  'bounceRate',
  'complaintRate',
  'unsubscribeRate',
];

type CampaignReportRatesProps = {
  rates: RateSet;
  trackOpens: boolean;
  trackClicks: boolean;
  deliveryDataAvailable: boolean;
  complaintThreshold: number;
};

export function CampaignReportRates({
  rates,
  trackOpens,
  trackClicks,
  deliveryDataAvailable,
  complaintThreshold,
}: CampaignReportRatesProps) {
  return (
    <div>
      <h3 className="mb-3 text-base font-semibold text-neutral-900">Rates</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {RATE_KEYS.map((key) => {
          const def = RATES[key];
          const notTracked =
            (key === 'openRate' && !trackOpens) ||
            ((key === 'clickRate' || key === 'clickToOpenRate') && !trackClicks);
          const hiddenDelivery = key === 'deliveryRate' && !deliveryDataAvailable;
          const value = rates[key];
          const digits = key === 'complaintRate' || key === 'unsubscribeRate' ? 2 : 1;
          const flagged = key === 'complaintRate' && value != null && value >= complaintThreshold;
          const display = notTracked ? 'Not tracked' : hiddenDelivery ? '—' : fmtPct(value, digits);
          return (
            <ReportKpiTile
              key={key}
              label={def.label}
              value={display}
              hint={hiddenDelivery ? 'Delivery data not received yet' : def.hint}
              flagged={flagged}
              className="rounded-xl p-3.5 [&_.text-2xl]:!text-xl"
            >
              {key === 'openRate' && trackOpens ? (
                <div className="mt-1.5 text-xs text-neutral-500">{OPEN_CAVEAT}</div>
              ) : null}
              {key === 'openRate' && !trackOpens ? (
                <div className="mt-1.5 text-xs text-neutral-500">
                  Opens are not tracked for this campaign.
                </div>
              ) : null}
              {key === 'complaintRate' && value != null ? (
                <div className="mt-1.5">
                  <span
                    className={
                      flagged
                        ? 'inline-flex rounded-full bg-[#FFF4E5] px-2 py-0.5 text-xs font-medium text-[#8A5B00]'
                        : 'inline-flex rounded-full bg-[#E8F7EE] px-2 py-0.5 text-xs font-medium text-[#0A7A3E]'
                    }
                  >
                    {flagged ? `≥ ${fmtPct(complaintThreshold, 2)} threshold` : 'Within threshold'}
                  </span>
                </div>
              ) : null}
            </ReportKpiTile>
          );
        })}
      </div>
    </div>
  );
}
